"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPut } from "@/lib/api-client";
import type { Product, Reservation } from "@/lib/api-client";
import { useAppStore } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { AvailabilityModal } from "@/components/booking/availability-modal";
import { ImageManagerModal } from "@/components/booking/image-manager-modal";
import { SeasonsModal } from "@/components/booking/seasons-modal";
import {
  Pencil,
  Trash2,
  CalendarRange,
  ImageIcon,
  Building2,
  Check,
  X,
  Mail,
  Phone,
  Clock,
  Power,
} from "lucide-react";
import {
  formatDate,
  formatPrice,
  nightsBetween,
  propertyTypeLabel,
  statusBadgeClass,
  statusLabel,
  nightLabel,
  bookingTypeLabel,
  bookingTypeBadgeClass,
} from "@/components/booking/format";
import { toast } from "sonner";
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useI18n } from "@/lib/i18n/provider";

export function HostDashboardView() {
  const user = useAppStore((s) => s.user);
  const openEditProduct = useAppStore((s) => s.openEditProduct);
  const openNewProduct = useAppStore((s) => s.openNewProduct);
  const openProduct = useAppStore((s) => s.openProduct);
  const qc = useQueryClient();
  const { t, locale } = useI18n();
  const [tab, setTab] = useState("properties");
  const [availFor, setAvailFor] = useState<string | null>(null);
  const [imagesFor, setImagesFor] = useState<string | null>(null);
  const [seasonsFor, setSeasonsFor] = useState<{ id: string; rate: number } | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data: productsData, isLoading: loadingProducts } = useQuery({
    queryKey: ["products", "manage"],
    queryFn: () => apiGet<{ products: Product[] }>(`/api/products?manage=1`),
    enabled: !!user && (user.role === "ADMIN" || user.role === "OWNER"),
  });

  const { data: resData, isLoading: loadingRes } = useQuery({
    queryKey: ["reservations", "host"],
    queryFn: () => apiGet<{ reservations: Reservation[] }>(`/api/reservations`),
    enabled: !!user && (user.role === "ADMIN" || user.role === "OWNER"),
  });

  const products = productsData?.products ?? [];
  const reservations = resData?.reservations ?? [];
  const pending = reservations.filter((r) => r.status === "PENDING");
  const others = reservations.filter((r) => r.status !== "PENDING");

  const decide = async (id: string, action: "approve" | "reject") => {
    try {
      await apiPatch(`/api/reservations/${id}`, { action });
      toast.success(action === "approve" ? t("toast.reservationApproved") : t("toast.reservationRejected"));
      qc.invalidateQueries({ queryKey: ["reservations"] });
      qc.invalidateQueries({ queryKey: ["products"] });
    } catch (err: any) {
      toast.error(err.message || t("toast.actionFailed"));
    }
  };

  const toggleActive = async (p: Product) => {
    try {
      await apiPut(`/api/products/${p.id}`, { active: !p.active });
      toast.success(p.active ? t("toast.propertyHidden") : t("toast.propertyPublished"));
      qc.invalidateQueries({ queryKey: ["products"] });
    } catch (err: any) {
      toast.error(err.message || t("toast.couldNotUpdate"));
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await apiDelete(`/api/products/${deleteId}`);
      toast.success(t("toast.propertyDeleted"));
      qc.invalidateQueries({ queryKey: ["products"] });
      qc.invalidateQueries({ queryKey: ["reservations"] });
    } catch (err: any) {
      toast.error(err.message || t("toast.couldNotDelete"));
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("host.title")}</h1>
          <p className="mt-1 text-muted-foreground">
            {user?.role === "ADMIN" ? t("host.subtitleAdmin") : t("host.subtitleOwner")}
          </p>
        </div>
        <Button onClick={openNewProduct} className="gap-2">
          <Building2 className="h-4 w-4" /> {t("host.listProperty")}
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="properties">
            {t("host.tabProperties")} ({products.length})
          </TabsTrigger>
          <TabsTrigger value="reservations">
            {t("host.tabReservations")} ({t("host.pendingCount", { count: pending.length })})
          </TabsTrigger>
        </TabsList>

        {/* PROPERTIES */}
        <TabsContent value="properties" className="mt-4 space-y-4">
          {loadingProducts ? (
            <div className="grid gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-32 w-full rounded-xl" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="grid place-items-center rounded-xl border border-dashed py-16 text-center">
              <Building2 className="mb-3 h-10 w-10 text-muted-foreground" />
              <p className="font-medium">{t("host.noPropertiesTitle")}</p>
              <p className="text-sm text-muted-foreground">{t("host.noPropertiesBody")}</p>
              <Button className="mt-4" onClick={openNewProduct}>
                {t("host.listProperty")}
              </Button>
            </div>
          ) : (
            products.map((p) => (
              <Card key={p.id}>
                <CardContent className="flex flex-col gap-4 p-4 lg:flex-row">
                  <div className="aspect-[4/3] w-full shrink-0 overflow-hidden rounded-lg bg-muted lg:w-48">
                    {p.images?.[0]?.url ? (
                       
                      <img
                        src={p.images[0].url}
                        alt={p.title}
                        className="h-full w-full cursor-pointer object-cover"
                        onClick={() => openProduct(p.id)}
                      />
                    ) : (
                      <div className="grid h-full w-full place-items-center text-xs text-muted-foreground">
                        {t("common.noImage")}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col gap-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <button
                          onClick={() => openProduct(p.id)}
                          className="text-left font-semibold hover:underline"
                        >
                          {p.title}
                        </button>
                        <p className="text-sm text-muted-foreground">{p.location}</p>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                          <Badge variant="outline">{propertyTypeLabel(p.propertyType, locale)}</Badge>
                          <Badge variant="outline">
                            {formatPrice(p.pricePerNight)} {t("card.perNight")}
                          </Badge>
                          {p.availabilities && p.availabilities.length > 0 ? (
                            <Badge variant="outline" className="border-emerald-200 text-emerald-700">
                              {t("host.availWindows", {
                                count: p.availabilities.length,
                                plural: p.availabilities.length !== 1 ? "s" : "",
                              })}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="border-amber-200 text-amber-700">
                              {t("host.noAvailabilitySet")}
                            </Badge>
                          )}
                          {p.active ? (
                            <Badge variant="outline" className="border-emerald-200 text-emerald-700">
                              {t("host.published")}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="border-zinc-300 text-zinc-500">
                              {t("host.hidden")}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" className="gap-1.5" onClick={() => openEditProduct(p.id)}>
                        <Pencil className="h-3.5 w-3.5" /> {t("common.edit")}
                      </Button>
                      <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setAvailFor(p.id)}>
                        <CalendarRange className="h-3.5 w-3.5" /> {t("host.availability")}
                      </Button>
                      <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setImagesFor(p.id)}>
                        <ImageIcon className="h-3.5 w-3.5" /> {t("host.photos", { count: p.images?.length ?? 0 })}
                      </Button>
                      <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setSeasonsFor({ id: p.id, rate: p.highSeasonRate ?? 0 })}>
                        <CalendarRange className="h-3.5 w-3.5" /> {t("season.ranges")}
                      </Button>
                      <Button size="sm" variant="outline" className="gap-1.5" onClick={() => toggleActive(p)}>
                        <Power className="h-3.5 w-3.5" /> {p.active ? t("common.hide") : t("common.publish")}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5 text-destructive hover:text-destructive"
                        onClick={() => setDeleteId(p.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" /> {t("common.delete")}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* RESERVATIONS */}
        <TabsContent value="reservations" className="mt-4 space-y-6">
          <section>
            <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
              <Clock className="h-4 w-4" /> {t("host.awaitingDecision")}
              <Badge variant="secondary">{pending.length}</Badge>
            </h3>
            {loadingRes ? (
              <Skeleton className="h-40 w-full rounded-xl" />
            ) : pending.length === 0 ? (
              <p className="rounded-xl border border-dashed py-10 text-center text-sm text-muted-foreground">
                {t("host.noPending")}
              </p>
            ) : (
              <div className="grid gap-3">
                {pending.map((r) => (
                  <ReservationRow
                    key={r.id}
                    r={r}
                    onApprove={() => decide(r.id, "approve")}
                    onReject={() => decide(r.id, "reject")}
                  />
                ))}
              </div>
            )}
          </section>

          <section>
            <h3 className="mb-3 text-sm font-semibold">{t("host.allRequests")}</h3>
            {others.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("host.noOther")}</p>
            ) : (
              <div className="grid gap-3">
                {others.map((r) => (
                  <ReservationRow key={r.id} r={r} readonly />
                ))}
              </div>
            )}
          </section>
        </TabsContent>
      </Tabs>

      <AvailabilityModal
        productId={availFor}
        open={!!availFor}
        onOpenChange={(o) => !o && setAvailFor(null)}
      />
      <ImageManagerModal
        productId={imagesFor}
        open={!!imagesFor}
        onOpenChange={(o) => !o && setImagesFor(null)}
      />
      <SeasonsModal
        productId={seasonsFor?.id ?? null}
        highSeasonRate={seasonsFor?.rate ?? 0}
        open={!!seasonsFor}
        onOpenChange={(o) => !o && setSeasonsFor(null)}
      />

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("host.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("host.deleteBody")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ReservationRow({
  r,
  onApprove,
  onReject,
  readonly,
}: {
  r: Reservation;
  onApprove?: () => void;
  onReject?: () => void;
  readonly?: boolean;
}) {
  const nights = nightsBetween(r.startDate, r.endDate);
  const openProduct = useAppStore((s) => s.openProduct);
  const { t, locale } = useI18n();
  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 gap-3">
          <div className="aspect-[4/3] w-20 shrink-0 overflow-hidden rounded-lg bg-muted">
            {r.product?.images?.[0]?.url ? (
               
              <img
                src={r.product.images[0].url}
                alt={r.product.title}
                className="h-full w-full cursor-pointer object-cover"
                onClick={() => r.product && openProduct(r.product.id)}
              />
            ) : null}
          </div>
          <div className="min-w-0">
            <button
              onClick={() => r.product && openProduct(r.product.id)}
              className="text-left font-medium hover:underline"
            >
              {r.product?.title ?? t("common.loading")}
            </button>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                {formatDate(r.startDate, locale)} → {formatDate(r.endDate, locale)} · {nights} {nightLabel(nights, locale)}
              </span>
              <span>· {r.partySize} {t("guests")}</span>
              <span>· {formatPrice(r.totalPrice)}</span>
              {r.bookingType === "SHARED" && (
                <Badge
                  variant="outline"
                  className={`border ${bookingTypeBadgeClass(r.bookingType)}`}
                >
                  {bookingTypeLabel(r.bookingType, locale)}
                </Badge>
              )}
            </div>
            <div className="mt-1 text-sm">
              <span className="font-medium">{r.guestName}</span>
              <span className="mx-1 text-muted-foreground">·</span>
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                <Mail className="h-3 w-3" /> {r.guestEmail}
              </span>
              {r.guestPhone && (
                <>
                  <span className="mx-1 text-muted-foreground">·</span>
                  <span className="inline-flex items-center gap-1 text-muted-foreground">
                    <Phone className="h-3 w-3" /> {r.guestPhone}
                  </span>
                </>
              )}
            </div>
            {r.notes && <p className="mt-1 text-sm italic text-muted-foreground">“{r.notes}”</p>}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Badge className={statusBadgeClass(r.status)}>{statusLabel(r.status, locale)}</Badge>
          {!readonly && r.status === "PENDING" && (
            <>
              <Button size="sm" onClick={onReject} variant="outline" className="gap-1">
                <X className="h-4 w-4" /> {t("host.decline")}
              </Button>
              <Button size="sm" onClick={onApprove} className="gap-1 bg-emerald-600 hover:bg-emerald-700">
                <Check className="h-4 w-4" /> {t("host.approve")}
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
